import React from "react"
import PropTypes from "prop-types"
import { CloseIcon, SuccessIcon, ErrorIcon, InfoIcon } from "./assets"
import Button from "../button"
import VisuallyHidden from "../visually-hidden"
import * as rht from "react-hot-toast"

const ICONS = {
  success: SuccessIcon,
  error: ErrorIcon,
  info: InfoIcon
}

// The convertkit-design build has trouble importing SVGs, so we've formatted
// the default icons as components. On the main app, it's cleaner to import an
// SVG and pass it into an <img src="" />, so we want to support both of these
// use cases. Once ck-design's build supports SVG imports, this can be removed.
const ImageOrSVG = ({ icon, alt, ...rest }) => {
  // If the icon is a React component...
  if (typeof icon === "function") {
    const IconComponent = icon
    return <IconComponent {...rest} />
  } else {
    // If the icon is imported...
    return <img src={icon} alt={alt} {...rest} />
  }
}

const getBarColor = type => {
  switch (type) {
    case "success":
      return "#589D78"
    case "error":
      return "var(--yellow-500)"
    case "info":
      return "var(--blue-500)"
    case "cta":
    default:
      return "var(--gray-100)"
  }
}

const ToastIcon = ({ type, icon }) => {
  const barStyles = {
    backgroundColor: getBarColor(type),
    borderRadius: 6,
    top: 6,
    left: 6,
    bottom: 6,
    width: 4
  }

  const iconStyles = {
    width: 26,
    height: 26
  }

  return (
    <div className="justify-center ml-6 mr-6 hidden md:flex">
      <div className="absolute inset-y-auto left-0" style={barStyles} />
      <ImageOrSVG
        icon={icon || ICONS[type]}
        alt=""
        style={iconStyles}
        className="ml-2"
      />
    </div>
  )
}

const Text = ({ title, text }) => {
  return (
    <div>
      {title && <div className="font-semibold text-gray-900 mb-1">{title}</div>}
      {text && <div className="text-gray-700">{text}</div>}
    </div>
  )
}

const Actions = ({ actions, handleClose }) => (
  <div className="ml-4">
    <div className="flex items-center gap-2">
      {actions.map(action => {
        // Closes the toast after an action button is clicked.
        const handleClick = async () => {
          if (!action.onClick) return
          await action.onClick()
          handleClose()
        }

        return (
          <Button
            key={action.text}
            is="primary"
            className="whitespace-no-wrap"
            {...action}
            onClick={handleClick}
          >
            {action.text}
          </Button>
        )
      })}
    </div>
  </div>
)

const Close = ({ handleClose }) => {
  const closeStyles = {
    width: 16,
    height: 16
  }

  return (
    <button onClick={handleClose} className="absolute top-0 right-0 p-2 block">
      <CloseIcon className="block" style={closeStyles} />
      <VisuallyHidden>Close notification</VisuallyHidden>
    </button>
  )
}

export function Toast({
  toast,
  type = "info",
  title,
  text,
  icon,
  actions,
  handleClose,
  closable
}) {
  return (
    <rht.ToastBar
      toast={toast}
      position="bottom-center"
      style={{ padding: 0, maxWidth: 500 }}
    >
      {() => (
        <div
          role="alert"
          className="rounded bg-white flex items-center text-left relative shadow-lg text-sm p-3 md:pr-8 md:pl-0 md:py-6"
        >
          <ToastIcon type={type} icon={icon} />
          <Text title={title} text={text} />
          {actions && <Actions actions={actions} handleClose={handleClose} />}
          {closable && <Close handleClose={handleClose} />}
        </div>
      )}
    </rht.ToastBar>
  )
}

Toast.propTypes = {
  toast: PropTypes.object,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType])
    .isRequired,
  type: PropTypes.oneOf(["info", "success", "error", "cta"]).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  actions: PropTypes.array,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  handleClose: PropTypes.func,
  closable: PropTypes.bool
}

/**
 * Displays a toast notification
 * @param {Object} options - Toast configuration options
 * @param {string} [options.id] - Optional unique identifier for the toast. If provided, only one toast with this ID can exist at a time. Calling toast() again with the same ID will update the existing toast.
 * @param {number|null} [options.duration] - Duration in milliseconds before the toast auto-dismisses. Use Infinity to keep the toast open indefinitely. Defaults to auto-dismiss.
 * @param {string} options.title - The title text to display in the toast
 * @param {string|React.Component} options.text - The body text or component to display in the toast (required)
 * @param {"info"|"success"|"error"|"cta"} options.type - The type of toast, which determines the color and default icon (required)
 * @param {string|React.Component} [options.icon] - Custom icon to display instead of the default type icon
 * @param {boolean} [options.closable] - Whether to show a close button on the toast
 * @param {Array<{text: string, onClick: Function}>} [options.actions] - Action buttons to display in the toast
 * @returns {string} The ID of the toast (either the provided ID or an auto-generated one)
 */
export function toast({ id, duration, ...rest }) {
  return rht.toast.custom(
    t => (
      <Toast toast={t} handleClose={() => rht.toast.dismiss(t.id)} {...rest} />
    ),
    { id, duration }
  )
}

/**
 * Dismisses a toast notification
 * @param {string} toastId - The ID of the toast to dismiss (either custom ID or the ID returned from toast())
 */
export function dismiss(toastId) {
  rht.toast.dismiss(toastId)
}

export function Toaster() {
  return <rht.Toaster position="bottom-center" />
}
